import { useCallback, useEffect, useRef } from 'react';

// Store all pending promises' resolve/reject callbacks

// Store a pending update with its associated promises

/**
 * Merges all elements in a queue of PendingUpdate into the first element.
 * The value from the last element in the queue becomes the value of the merged element.
 * All promises from all elements are appended to the first element.
 *
 * @param queue The queue of PendingUpdate to merge
 * @returns The merged PendingUpdate array with a single element, or the original queue if it has 0-1 elements
 */
function mergeQueue(queue) {
  if (queue.length <= 1) {
    return queue;
  }

  // Get the first element as our target
  const firstUpdate = queue[0];

  // Get the value from the last element (last value wins)
  const lastUpdate = queue[queue.length - 1];
  firstUpdate.value = lastUpdate.value;

  // Collect all promises from other updates
  for (let i = 1; i < queue.length; i++) {
    firstUpdate.promises.push(...queue[i].promises);
  }

  // Return the merged update
  return [firstUpdate];
}

/**
 * Creates a Promise with externally accessible resolve/reject functions
 * @returns An object containing the promise and its resolve/reject functions
 */
function createPromise() {
  let resolve;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return {
    promise,
    resolve,
    reject
  };
}

/**
 * Function type for updating a value based on its previous state
 */

/**
 * A generic hook for auto-batching updates with debouncing
 *
 * @param applyFn Function to apply the batched updates (must return a Promise)
 * @param debounceTime Time in milliseconds to debounce updates (default: 0)
 * @returns A tuple containing:
 *   - queueUpdate: Function to queue updates for batching
 *   - flush: Function to manually flush the next pending update
 *
 * How it works:
 * 1. Successive updates are accumulated into a single entry in the pending update queue
 * 2. When the debounce window ends (via timeout or manual flush), the next pending update is processed
 * 3. If applyFn fails, all pending updates are merged into a single update for the next flush attempt
 * 4. Updates are processed in FIFO order (first in, first out)
 * 5. Within a single batch, the last value wins, but all promises are resolved with the result
 *
 * Note: If your applyFn is synchronous, wrap it in Promise.resolve() before passing it to this hook.
 */
export function useAutoBatch(applyFn, debounceTime = 0) {
  // Queue of pending updates
  const pendingQueueRef = useRef([]);
  // Reference to the current debounce timeout
  const timeoutRef = useRef(null);

  /**
   * Flushes the next pending update in the queue
   * @returns A promise that resolves with the result of the update, or void if no updates are pending
   */
  const flush = useCallback(() => {
    // Clear the timeout
    clearTimeout(timeoutRef.current);
    timeoutRef.current = null;

    // If there are no pending updates, return a resolved promise
    if (pendingQueueRef.current.length === 0) {
      return Promise.resolve();
    }

    // Always merge all pending updates to use the latest accumulated value
    pendingQueueRef.current = mergeQueue(pendingQueueRef.current);

    // Get the merged update (now guaranteed to be the latest value)
    const {
      value,
      promises
    } = pendingQueueRef.current[0];

    // Apply the update and handle the result
    return applyFn(value).then(
    // Success handler
    result => {
      // Resolve all promises with the result
      promises.forEach(({
        resolve
      }) => resolve(result));
      // Remove the processed update from the queue
      pendingQueueRef.current.shift();
      return result;
    },
    // Error handler
    error => {
      // Reject all promises with the error
      promises.forEach(({
        reject
      }) => reject(error));
      // Merge all remaining updates into a single update for the next attempt
      pendingQueueRef.current = mergeQueue(pendingQueueRef.current);
      throw error;
    });
  }, [applyFn]);

  /**
   * Queues an update to be applied after the debounce window
   * @param updater Function that produces the new value based on the current value
   * @returns A promise that resolves with the result of the update
   */
  const queueUpdate = useCallback(updater => {
    var _currentBatch$value;
    // Get the current batch if there is one
    const currentBatch = pendingQueueRef.current[pendingQueueRef.current.length - 1];
    // Get the current value to base the update on
    const currentValue = (_currentBatch$value = currentBatch === null || currentBatch === void 0 ? void 0 : currentBatch.value) !== null && _currentBatch$value !== void 0 ? _currentBatch$value : null;

    // Create a promise that will be resolved when this update is processed
    const {
      promise,
      resolve,
      reject
    } = createPromise();
    const newValue = updater(currentValue);
    if (!timeoutRef.current) {
      // No active timeout, create a new batch
      const newBatch = {
        value: newValue,
        promises: [{
          resolve,
          reject
        }]
      };
      pendingQueueRef.current.push(newBatch);
    } else {
      // Active timeout, update the current batch
      currentBatch.value = newValue;
      currentBatch.promises.push({
        resolve,
        reject
      });
    }

    // Cancel any existing timeout to extend the debounce window
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Schedule a flush after the debounce time
    timeoutRef.current = setTimeout(() => {
      flush().catch(console.error);
    }, debounceTime);
    return promise;
  }, [flush, debounceTime]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  return [queueUpdate, flush];
}